import React, { useEffect, useMemo, useState } from "react";
import ReceiptModal from "./ReceiptModal";
import useWalletContext from "./useWalletContext";

function debounceTimer() {
  let t = null;
  return {
    set(fn, ms) {
      if (t) clearTimeout(t);
      t = setTimeout(fn, ms);
    },
    clear() {
      if (t) clearTimeout(t);
      t = null;
    },
  };
}

export default function MoneyTransferForm({ onSuccess }) {
  const { ctx } = useWalletContext();

  const [recipientName, setRecipientName] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [recipientUserId, setRecipientUserId] = useState(null);

  const [lookupStatus, setLookupStatus] = useState({ loading: false, found: false, self: false, msg: "" });

  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");

  const [loading, setLoading] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [error, setError] = useState("");

  const [receipt, setReceipt] = useState(null);
  const [openReceipt, setOpenReceipt] = useState(false);

  async function fetchWallet() {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/wallet`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to fetch wallet");

      // CAD-only display: keep your existing shape
      setWallet({
        availableCad: data.wallet?.availableCad ?? null,
        balanceCad: data.wallet?.balanceCad ?? null,
        heldCad: data.wallet?.heldCad ?? null,
        displayBalance: data.display?.balance ?? null,
        displayCurrency: data.display?.currency ?? (ctx?.displayCurrency || "CAD"),
      });
    } catch (err) {
      console.error(err);
      setError("Impossible de récupérer les informations du wallet.");
    }
  }

  useEffect(() => {
    fetchWallet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Lookup recipient by phone (debounced)
  const deb = useMemo(() => debounceTimer(), []);
  useEffect(() => {
    const phone = String(recipientPhone || "").trim();
    setRecipientUserId(null);

    if (!phone || phone.length < 6) {
      setLookupStatus({ loading: false, found: false, self: false, msg: "" });
      return;
    }

    setLookupStatus((p) => ({ ...p, loading: true, msg: "" }));

    deb.set(async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/wallet/lookup-user?phone=${encodeURIComponent(phone)}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } }
        );

        const data = await res.json();
        if (!res.ok) throw new Error(data.message || "Lookup failed");

        if (data?.found && data?.self) {
          setLookupStatus({ loading: false, found: true, self: true, msg: "Vous ne pouvez pas vous transférer à vous-même." });
          setRecipientUserId(null);
          return;
        }

        if (data?.found && data?.user) {
          setRecipientUserId(data.user.id);
          setRecipientName(data.user.fullName || "");
          setLookupStatus({ loading: false, found: true, self: false, msg: `Bénéficiaire trouvé: ${data.user.fullName}` });
          return;
        }

        setLookupStatus({ loading: false, found: false, self: false, msg: "Aucun utilisateur trouvé pour ce numéro." });
        setRecipientUserId(null);
      } catch (e) {
        setLookupStatus({ loading: false, found: false, self: false, msg: e.message || "Erreur lookup" });
        setRecipientUserId(null);
      }
    }, 450);

    return () => deb.clear();
  }, [recipientPhone, deb]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    const amountNumber = parseFloat(amount);
    if (!Number.isFinite(amountNumber) || amountNumber <= 0) {
      setError("Veuillez saisir un montant valide.");
      return;
    }

    if (!recipientPhone.trim()) {
      setError("Veuillez renseigner le téléphone du bénéficiaire.");
      return;
    }

    // ✅ enforce wallet-to-wallet internal transfer
    if (!recipientUserId) {
      setError("Bénéficiaire introuvable. Veuillez utiliser un numéro associé à un compte NGC.");
      return;
    }

    if (!recipientName.trim()) {
      setError("Veuillez renseigner le nom du bénéficiaire.");
      return;
    }

    setLoading(true);

    try {
      const idempotencyKey = crypto?.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random()}`;

      const payload = {
        recipientName: recipientName.trim(),
        recipientPhone: recipientPhone.trim(),
        recipientUserId, // ✅ send to backend
        amount: amountNumber,
        note: note?.trim() || "",
        currency: "CAD", // ✅ CAD-only
      };

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/wallet/transfer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          "Idempotency-Key": idempotencyKey,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Le transfert a échoué");

      if (data.receipt) {
        setReceipt(data.receipt);
        setOpenReceipt(true);
      }

      await fetchWallet();

      setRecipientName("");
      setRecipientPhone("");
      setRecipientUserId(null);
      setAmount("");
      setNote("");

      onSuccess && onSuccess();
    } catch (err) {
      setError(err.message || "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  }

  const displayCurrency = "CAD"; // CAD-only

  return (
    <section className="rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm p-5 max-w-xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold text-slate-900 dark:text-white">Envoyer de l'argent</h3>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Transfert interne (soumis à validation)</p>
        </div>

        <div className="rounded-2xl border border-black/5 dark:border-white/10 bg-slate-50 dark:bg-white/5 px-3 py-2">
          <p className="text-xs text-slate-600 dark:text-slate-300">
            Solde:{" "}
            <strong className="text-slate-900 dark:text-white">
              {wallet?.availableCad != null ? `${Number(wallet.availableCad).toFixed(2)} CAD` : "—"}
            </strong>
          </p>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            Held: {wallet?.heldCad ?? "—"} CAD
          </p>
        </div>
      </div>

      {(error || lookupStatus.msg) && (
        <div
          className={`mt-4 rounded-2xl border px-4 py-3 text-sm ${
            error || lookupStatus.self
              ? "border-red-500/20 bg-red-500/10 text-red-700 dark:text-red-300"
              : lookupStatus.found
              ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200"
              : "border-amber-500/20 bg-amber-500/10 text-amber-800 dark:text-amber-200"
          }`}
        >
          {error || lookupStatus.msg}
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-4 space-y-4">
        <Field label="Téléphone du bénéficiaire">
          <div className="relative">
            <input
              value={recipientPhone}
              onChange={(e) => setRecipientPhone(e.target.value)}
              required
              className={inputCls}
              placeholder="Ex: +2376xxxxxxx"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 dark:text-slate-400">
              {lookupStatus.loading ? "Recherche…" : lookupStatus.found ? "✅" : ""}
            </div>
          </div>
        </Field>

        <Field label="Nom du bénéficiaire">
          <input
            value={recipientName}
            onChange={(e) => setRecipientName(e.target.value)}
            required
            className={inputCls}
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Auto-rempli si le numéro correspond à un compte.
          </p>
        </Field>

        <Field label={`Montant (${displayCurrency})`}>
          <input
            type="number"
            min="1"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
            className={inputCls}
          />
        </Field>

        <Field label="Note (optionnelle)">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className={inputCls}
            placeholder="Optionnel"
          />
        </Field>

        <button type="submit" disabled={loading || !wallet} className={primaryBtn}>
          {loading ? "Envoi..." : "Envoyer (en attente)"}
        </button>
      </form>

      <ReceiptModal
        open={openReceipt}
        receipt={receipt}
        onClose={() => {
          setOpenReceipt(false);
          setReceipt(null);
        }}
      />
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

const inputCls =
  "w-full rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-slate-950 px-4 py-3 text-slate-900 dark:text-white outline-none " +
  "focus:ring-2 focus:ring-slate-900/10 dark:focus:ring-white/10";

const primaryBtn =
  "w-full rounded-2xl bg-slate-900 text-white py-3 font-medium hover:opacity-95 transition disabled:opacity-60 dark:bg-white dark:text-slate-900";
